import { apiClient } from './client';

export interface FieldDefinition {
  id: string;
  companyId: string;
  name: string;
  slug: string;
  fieldType: 'text' | 'textarea' | 'select' | 'number' | 'boolean' | 'date' | 'url';
  label: string;
  placeholder?: string | null;
  helpText?: string | null;
  options: { value: string; label: string }[];
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
  _count?: { productLineFields: number };
}

export interface ProductLineField {
  id: string;
  productLineId: string;
  fieldDefinitionId: string;
  isRequired: boolean;
  sortOrder: number;
  fieldDefinition: FieldDefinition;
}

export interface ProductLine {
  id: string;
  companyId: string;
  name: string;
  description?: string | null;
  color: string;
  icon?: string | null;
  isActive: boolean;
  enabledFields: string[]; // built-in field keys
  emailKeywords: string[]; // inbound-email routing keywords
  sortOrder: number;
  productLineFields: ProductLineField[];
  _count?: { tickets: number };
}

// ── Built-in ticket field keys that can be toggled per product line ────────────
export const BUILTIN_FIELDS: { key: string; label: string; group: string }[] = [
  // Classification
  { key: 'impact',          label: 'Business Impact',   group: 'Classification' },
  { key: 'environment',     label: 'Environment',        group: 'Classification' },
  // Product & schedule
  { key: 'product',         label: 'Product / Service',  group: 'Product & schedule' },
  { key: 'productVersion',  label: 'Version',            group: 'Product & schedule' },
  { key: 'department',      label: 'Department',         group: 'Product & schedule' },
  { key: 'location',        label: 'Location',           group: 'Product & schedule' },
  { key: 'dueDate',         label: 'Due Date',           group: 'Product & schedule' },
  { key: 'estimatedHours',  label: 'Estimated Hours',    group: 'Product & schedule' },
  // Technical details
  { key: 'browser',         label: 'Browser',            group: 'Technical details' },
  { key: 'device',          label: 'Device',             group: 'Technical details' },
  { key: 'operatingSystem', label: 'Operating System',   group: 'Technical details' },
  { key: 'reproducibility', label: 'Reproducibility',    group: 'Technical details' },
  { key: 'stepsToReproduce',label: 'Steps to Reproduce', group: 'Technical details' },
];

export const productLinesApi = {
  list: async (): Promise<ProductLine[]> => {
    const res = await apiClient.get('/product-lines');
    return res.data.data.productLines;
  },

  get: async (id: string): Promise<ProductLine> => {
    const res = await apiClient.get(`/product-lines/${id}`);
    return res.data.data.productLine;
  },

  create: async (data: {
    name: string;
    description?: string;
    color?: string;
    icon?: string;
    enabledFields?: string[];
    emailKeywords?: string[];
    sortOrder?: number;
  }): Promise<ProductLine> => {
    const res = await apiClient.post('/product-lines', data);
    return res.data.data.productLine;
  },

  update: async (id: string, data: Partial<{
    name: string;
    description: string | null;
    color: string;
    icon: string | null;
    enabledFields: string[];
    emailKeywords: string[];
    sortOrder: number;
    isActive: boolean;
  }>): Promise<ProductLine> => {
    const res = await apiClient.put(`/product-lines/${id}`, data);
    return res.data.data.productLine;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/product-lines/${id}`);
  },

  setFields: async (id: string, fields: { fieldDefinitionId: string; isRequired?: boolean; sortOrder?: number }[]): Promise<ProductLine> => {
    const res = await apiClient.put(`/product-lines/${id}/fields`, { fields });
    return res.data.data.productLine;
  },
};

export const fieldDefinitionsApi = {
  list: async (): Promise<FieldDefinition[]> => {
    const res = await apiClient.get('/field-definitions');
    return res.data.data.fieldDefinitions;
  },

  create: async (data: {
    name: string;
    slug: string;
    fieldType: FieldDefinition['fieldType'];
    label: string;
    placeholder?: string;
    helpText?: string;
    options?: { value: string; label: string }[];
    isRequired?: boolean;
    sortOrder?: number;
  }): Promise<FieldDefinition> => {
    const res = await apiClient.post('/field-definitions', data);
    return res.data.data.fieldDefinition;
  },

  update: async (id: string, data: Partial<FieldDefinition>): Promise<FieldDefinition> => {
    const res = await apiClient.put(`/field-definitions/${id}`, data);
    return res.data.data.fieldDefinition;
  },

  delete: async (id: string, force = false): Promise<void> => {
    await apiClient.delete(`/field-definitions/${id}`, { params: force ? { force: 'true' } : undefined });
  },
};
